'use client';

import { type ReactNode, useEffect, useState } from 'react';

import type { HomeAppShot, HomeAppShotId } from '../../_content/home';
import { MARKETING_IMG, idx } from '../../_lib/marketing-utils';
import { MarketingImage } from '../ui/marketing-image';
import './app-showcase.css';

/** Wie lange ein Screen steht, bevor die Diashow weiterschaltet (J9). */
const SLIDE_MS = 7000;

/**
 * Bild, Beschnitt und Grösse je Screen — sprachunabhängig, deshalb hier und
 * nicht im Inhalt (der DE-Screenshot steht bewusst in allen Sprachen, wie
 * zuvor beim Produkt-Blick).
 *
 * Je Screen zwei Aufnahmen (30.10.2026): die Rechner-Fassung (1440 px breit,
 * doppelte Auflösung) ab 768 px und die Telefon-Fassung (390 × 844, doppelte
 * Auflösung) darunter. Ein verkleinerter Rechner-Screen ist am Telefon nicht
 * lesbar. Aufgenommen aus der App im aktuellen Design, im Vorführ-Arbeitsbereich
 * mit erfundenen Personen; Anzeigename, Bild und Mailadressen ersetzt.
 *
 * 🔴 Die Beschnitt-Klasse ist kein Beiwerk. Die Rechner-Aufnahmen sind
 * 1440-Viewport-Frames mit sehr verschiedenen Höhen; ohne die eigene Klasse
 * erbt ein Querformat-Screen den Hochformat-Beschnitt der Grundregel
 * (`aspect-ratio: 1440 / 1650` in marketing-extra.css). Breite und Höhe hier
 * und die `aspect-ratio` dort sind dieselbe Zahl, aus dem Bildkopf gemessen —
 * wer eine ändert, ändert beide.
 *
 * 🔴 Der Dateiname wandert bei jedem Tausch mit (…-v6, …-v3): die Dateien
 * liegen unter `public/images/**` und werden mit langer Frist ausgeliefert.
 * Ein Tausch am gleichen Namen ließe Bestandsbesucher das alte Bild sehen.
 */
const SHOT_ASSETS: Record<
  HomeAppShotId,
  {
    file: string;
    shotClass: string;
    width: number;
    height: number;
  }
> = {
  vermietung: {
    file: 'vermietung-desktop-v6.webp',
    shotClass: 'shot--vermietung',
    width: 2880,
    height: 4680,
  },
  startseite: {
    file: 'startseite-desktop-v3.webp',
    shotClass: 'shot--startseite',
    width: 2880,
    height: 2460,
  },
  bewerberprofil: {
    file: 'bewerber-profil-desktop-v5.webp',
    shotClass: 'shot--bewerberprofil',
    width: 2880,
    height: 2142,
  },
};

/**
 * Mittleres Bild (Breite / Höhe in Pixeln, zweithöchster Screen) — gibt dem
 * Rahmen die feste Fläche. Der höhere Screen wird unten beschnitten.
 */
const SLOT_ASSET = Object.values(SHOT_ASSETS).sort(
  (a, b) => b.height / b.width - a.height / a.width,
)[1]!;

/**
 * Home-Abschnitt „Aus der App" (UMSETZUNG.md §4, J3 + J9).
 *
 * Echte App-Screens, gerahmt im Marketing-Karten-Look — kein App-Chrome
 * nachgebaut, kein App-Design-System in den Marketing-Seiten (§7). Umschalter
 * mit `role="tab"`/`aria-selected`; jeder Reiter zeigt genau einen Screen.
 *
 * Diashow: wechselt alle 7 s, hält beim Überfahren der Reiter an und stoppt
 * DAUERHAFT, sobald jemand selbst einen Reiter wählt — danach entscheidet der
 * Nutzer, nicht der Timer. Bei `prefers-reduced-motion: reduce` läuft sie gar
 * nicht erst an.
 *
 * 🔴 Kein `reveal` auf der gewechselten Figur. Der `RevealController` scannt
 * einmal je Route; ein `.reveal`, das erst beim Reiterwechsel in den Baum
 * kommt, bekäme nie `is-visible` und bliebe auf `opacity: 0`. Deshalb trägt
 * der stehende Rahmen das `reveal`, nicht die wechselnde Figur.
 */
export function AppShowcase({
  eyebrow,
  headline,
  lead,
  tabsAria,
  shots,
}: {
  eyebrow: string;
  headline: ReactNode;
  lead: string;
  tabsAria: string;
  shots: HomeAppShot[];
}) {
  const [active, setActive] = useState(0);
  const [manual, setManual] = useState(false);
  const [paused, setPaused] = useState(false);

  useEffect(() => {
    if (manual || paused) return;
    if (typeof window === 'undefined' || !window.matchMedia) return;
    if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) return;

    const timer = window.setInterval(
      () => setActive((cur) => (cur + 1) % shots.length),
      SLIDE_MS,
    );

    return () => window.clearInterval(timer);
  }, [manual, paused, shots.length]);

  const shot = shots[active];
  const asset = shot ? SHOT_ASSETS[shot.id] : undefined;

  return (
    <section
      className="section app-section"
      id="app"
      aria-labelledby="app-headline"
    >
      <div className="container">
        <div style={{ maxWidth: '680px' }}>
          <span className="eyebrow reveal">{eyebrow}</span>
          <h2 className="h-display-md reveal" style={idx(1)} id="app-headline">
            {headline}
          </h2>
          <p className="lead reveal" style={idx(2)}>
            {lead}
          </p>
        </div>

        <div
          className="appshot-tabs reveal"
          style={idx(3)}
          role="tablist"
          aria-label={tabsAria}
          onMouseEnter={() => setPaused(true)}
          onMouseLeave={() => setPaused(false)}
        >
          {shots.map((s, i) => (
            <button
              key={s.id}
              id={`appshot-tab-${s.id}`}
              type="button"
              className="appshot-tab"
              role="tab"
              aria-selected={i === active}
              aria-controls="appshot-panel"
              onClick={() => {
                setActive(i);
                setManual(true);
              }}
            >
              {s.tabLabel}
            </button>
          ))}
        </div>

        <div
          className="appshot appshot--wide reveal"
          style={idx(4)}
          id="appshot-panel"
          role="tabpanel"
          aria-labelledby={shot ? `appshot-tab-${shot.id}` : undefined}
        >
          {shot && asset ? (
            <figure className="appshot-figure">
              {/* 🔴 Feste Fläche in der Höhe des mittleren Screens: die Seiten-
                  höhe ändert sich beim Wechsel nie (Layout Shift). Der hohe
                  Screen wird unten beschnitten, der flachere steht oben. */}
              <div
                className="appshot-slot"
                style={{
                  aspectRatio: `${SLOT_ASSET.width} / ${SLOT_ASSET.height}`,
                }}
              >
                <MarketingImage
                  className={asset.shotClass}
                  src={`${MARKETING_IMG}/app/${asset.file}`}
                  width={asset.width}
                  height={asset.height}
                  /* 🔴 KEIN `key` auf der Figur oder dem Bild — das ist gewollt.
                   Ohne `key` behält React denselben <img>-Knoten und patcht
                   nur `src`/`class`; der Browser lädt das neue Bild sofort.
                   Mit `key` käme bei jedem Reiterwechsel ein FRISCHER Knoten
                   in den Baum, und für nachträglich eingefügte Knoten wird
                   der Lazy-Kandidat nicht mehr ausgewertet — der Fetch würde
                   nie starten und die Karte bliebe leer (J9, dort belegt).
                   `lazy` steht deshalb nur am zuerst gezeigten Screen: der
                   Abschnitt liegt weit unter der Falz, und beim Wechsel ist
                   der Knoten längst im Sichtfeld. */
                  loading={active === 0 ? 'lazy' : undefined}
                  decoding="async"
                  alt={shot.alt}
                />
              </div>
              <figcaption>
                <span className="appshot-badge">{shot.badge}</span>
                <strong>{shot.strong}</strong>
                {shot.rest}
              </figcaption>
            </figure>
          ) : null}
        </div>
      </div>
    </section>
  );
}
